import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	List,
	ListItem,
	Radio,
	RadioGroup,
	Stack,
	Typography,
} from '@mui/material';
import {AlertInfo} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {DocumentEnum} from 'presentation/pages/private/refund/constants/documentEnum';
import {useSelectedDoctorStore} from 'presentation/pages/private/refund/pages/Request/store/useSelectedDoctorStore';
import {useSelectedProviderStore} from 'presentation/pages/private/refund/pages/Request/store/useSelectedProviderStore';
import {translations} from 'presentation/translations-new';
import {formatMasks} from 'presentation/utils';
import React, {Fragment, useEffect, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {DoctorListItem} from '../../../../DoctorListItem';
import {DoctorFields} from '../DoctorFields';
import {SearchProfessional} from '../SearchProfessional';
import {FooterFormProps} from './types';

const {
	search,
	seeMore: seeMoreText,
	invalidSelectProvider,
	invalidSelectProfessional,
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData;

export const FooterForm = ({
	doctorNotFound,
	professionalNotFound,
	onSubmit,
	doctorList,
	doctorsListProfessional,
	isLoading,
	isLoadingProfessional,
	personType,
	seeMore,
	searchDoctorByWatch,
	totalItems,
	totalItemsProfessional,
	setDoctorList,
	setDoctorsListProfessional,
	documentType,
	onReset,
	onResetProfessionalList,
}: FooterFormProps) => {
	const {
		setValue,
		trigger,
		watch,
		formState: {errors},
		clearErrors,
	} = useFormContext();
	const selectedDoctor = useSelectedDoctorStore(state => state.doctor);
	const setSelectedDoctor = useSelectedDoctorStore(state => state.setDoctor);
	const selectedProvider = useSelectedProviderStore(state => state.provider);
	const setSelectedProvider = useSelectedProviderStore(state => state.setProvider);

	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isLoadingMoreProfessional, setIsLoadingMoreProfessional] = useState(false);
	const [shouldRegisterOtherProvider, setShouldRegisterOtherProvider] = useState(false);
	const [page, setPage] = useState(1);
	const [pageProfessional, setPageProfessional] = useState(1);

	const isSearchByLegalPerson = documentType === DocumentEnum.CNPJ;
	const hasMore = totalItems > doctorList.length;

	const hasMoreProfessional =
		doctorsListProfessional && totalItemsProfessional
			? totalItemsProfessional > doctorsListProfessional.length
			: false;
	const showDoctorList = doctorList && doctorList?.length > 0 && !doctorNotFound;
	const professionalDocument = watch('professionalDocument') as string;
	const providerName = watch('providerName') as string;
	const councilAcronym = watch('councilAcronym');
	const councilNumber = watch('councilNumber');
	const councilState = watch('councilState');

	const showProfessionalInProviderList =
		!isLoading && isSearchByLegalPerson && (selectedProvider || providerName?.length);

	async function handleAddMoreToList() {
		setIsLoadingMore(true);
		if (hasMore) {
			setPage(prevPage => prevPage + 1);
		}
	}
	async function handleAddMoreToListProfessional() {
		setIsLoadingMoreProfessional(true);
		if (hasMoreProfessional) {
			setPageProfessional(prevPage => prevPage + 1);
		}
	}

	async function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
		const selectedDoctorDocument = event.target.value;
		const doctor = doctorList?.find(
			item => `${item.document}-${item.providerCode}` === selectedDoctorDocument,
		);
		setSelectedDoctor(doctor || null);
		setValue('providerName', doctor?.providerName);
		setValue('professionalName', doctor?.performerName ?? doctor?.providerName);
		setValue('councilAcronym', doctor?.councilAcronym);
		setValue('providerCode', doctor?.providerCode);
		setValue('councilNumber', doctor?.councilNumber);
		setValue('councilState', doctor?.councilState);
		setValue('professionalCpf', selectedDoctorDocument.split('-')[0]);
		await trigger();
		clearErrors();
	}

	async function handleChangeProvider(event: React.ChangeEvent<HTMLInputElement>) {
		const selectedProviderDocument = event.target.value;
		const provider = doctorList?.find(
			item => `${item.document}-${item.providerCode}` === selectedProviderDocument,
		);
		setSelectedProvider(provider || null);
		setValue('providerName', provider?.providerName);
		setValue('providerCode', provider?.providerCode);
		setValue('professionalDocument', selectedProviderDocument.split('-')[0]);
		setValue('professionalCpf', '');
		await trigger();
		clearErrors();
	}

	useEffect(() => {
		async function fetchData() {
			const sanitizedDocument = professionalDocument
				.replace(/[^A-Za-z0-9]/g, '')
				.toUpperCase();
			const isCnpj = sanitizedDocument.length === 14;
			const response = await seeMore({
				page,
				document: sanitizedDocument,
				'council-acronym': isCnpj ? undefined : councilAcronym?.acronym,
				'council-number': isCnpj ? undefined : councilNumber,
				state: isCnpj ? undefined : councilState?.code,
			});
			setDoctorList([...doctorList, ...response.content.providers]);
			setIsLoadingMore(false);
		}

		if (page > 1) {
			fetchData();
		}
	}, [page]);

	useEffect(() => {
		async function fetchDataProfessional() {
			const sanitizedDocument = professionalDocument
				.replace(/[^A-Za-z0-9]/g, '')
				.toUpperCase();
			const isCnpj = sanitizedDocument.length === 14;
			const response = await seeMore({
				page: pageProfessional,
				document: isCnpj ? undefined : sanitizedDocument,
				'council-acronym': councilAcronym?.acronym,
				'council-number': councilNumber,
				state: councilState?.code,
			});
			if (setDoctorsListProfessional && doctorsListProfessional) {
				setDoctorsListProfessional([
					...doctorsListProfessional,
					...response.content.providers,
				]);
			}
			setIsLoadingMoreProfessional(false);
		}

		if (pageProfessional > 1) {
			fetchDataProfessional();
		}
	}, [pageProfessional]);

	return (
		<Fragment>
			{!doctorNotFound ? (
				<Button
					data-tag-id={RefundTaggingEnum.CLICK_PESQUISAR_PROFISSIONAL}
					variant="outlined"
					color="primary"
					onClick={() => {
						onSubmit();
						setShouldRegisterOtherProvider(false);
						setPage(1);
					}}
					disabled={isLoading}
					endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}>
					{search}
				</Button>
			) : (
				<DoctorFields
					personType={personType}
					doctorNotFound={doctorNotFound}
					searchDoctorByWatch={searchDoctorByWatch}
					registerOtherProviderFlow={shouldRegisterOtherProvider}
				/>
			)}
			{showDoctorList && isSearchByLegalPerson ? (
				<FormControl>
					<List>
						{errors?.providerName ? (
							<AlertInfo.Root severity="error" sx={{marginTop: '-8px'}}>
								<AlertInfo.Description>
									<Typography variant="body2">{invalidSelectProvider}</Typography>
								</AlertInfo.Description>
							</AlertInfo.Root>
						) : null}
						{doctorList ? (
							<RadioGroup
								aria-labelledby="demo-controlled-radio-buttons-group"
								name="controlled-radio-buttons-group"
								value={
									selectedProvider
										? `${selectedProvider?.document}-${selectedProvider?.providerCode}`
										: ''
								}
								onSelect={() => clearErrors}
								onChange={handleChangeProvider}>
								{doctorList.map((doctor, index) => {
									const isCNPJ = doctor?.document?.length === 14;

									return (
										<ListItem
											key={`${doctor?.document}-${doctor?.providerCode}`}
											divider={index !== (doctorList?.length ?? 1) - 1}>
											<FormControlLabel
												value={`${doctor?.document}-${doctor?.providerCode}`}
												control={<Radio />}
												label={
													<Stack>
														<Typography variant="body1">
															{doctor?.providerName}
														</Typography>
														<Typography
															variant="body2"
															color="text.secondary">
															{isCNPJ
																? `CNPJ ${formatMasks('cnpj', doctor?.document)}`
																: `${doctor?.councilNumber}/${doctor?.councilAcronym.acronym}-${doctor?.councilState.code} - CPF ${formatMasks('cpf', doctor?.document)}`}
														</Typography>
													</Stack>
												}
											/>
										</ListItem>
									);
								})}
							</RadioGroup>
						) : null}
					</List>

					{hasMore && !selectedProvider && (
						<Box width="100%" display="flex" justifyContent="center">
							<Button
								variant="text"
								onClick={handleAddMoreToList}
								disabled={isLoadingMore}
								endIcon={
									isLoadingMore ? <Loading size={16} color="inherit" /> : null
								}>
								{seeMoreText}
							</Button>
						</Box>
					)}
				</FormControl>
			) : null}
			{showProfessionalInProviderList ? (
				<SearchProfessional
					isLoadingProfessional={isLoadingProfessional}
					doctorsListProfessional={doctorsListProfessional}
					hasMore={hasMoreProfessional}
					handleAddMoreToList={handleAddMoreToListProfessional}
					isLoadingMore={isLoadingMoreProfessional}
					onSubmit={onSubmit}
					searchDoctorByWatch={searchDoctorByWatch}
					onReset={onReset}
					onResetProfessionalList={searchInProvider => {
						if (onResetProfessionalList) {
							onResetProfessionalList(searchInProvider);
						}
						setPageProfessional(1);
					}}
					professionalNotFound={professionalNotFound}
					setPageProfessional={setPageProfessional}
				/>
			) : null}
			{showDoctorList && !isSearchByLegalPerson ? (
				<FormControl>
					{errors?.professionalName ? (
						<AlertInfo.Root severity="error" sx={{marginTop: '8px'}}>
							<AlertInfo.Description>
								<Typography variant="body2">{invalidSelectProfessional}</Typography>
							</AlertInfo.Description>
						</AlertInfo.Root>
					) : null}
					<List>
						{doctorList ? (
							<RadioGroup
								aria-labelledby="demo-controlled-radio-buttons-group"
								name="controlled-radio-buttons-group"
								value={
									selectedDoctor
										? `${selectedDoctor?.document}-${selectedDoctor?.providerCode}`
										: ''
								}
								onChange={handleChange}>
								{doctorList.map((doctor, index) => (
									<DoctorListItem
										key={`${doctor?.document}-${doctor?.providerCode}`}
										doctor={doctor}
										isLastItem={index === (doctorList?.length ?? 1) - 1}
										selectedValue={
											selectedProvider
												? `${selectedProvider?.document}-${selectedProvider?.providerCode}`
												: ''
										}
										onChange={handleChangeProvider}
									/>
								))}
							</RadioGroup>
						) : null}
					</List>

					{hasMore ? (
						<Box width="100%" display="flex" justifyContent="center">
							<Button
								variant="text"
								onClick={handleAddMoreToList}
								disabled={isLoadingMore}
								endIcon={
									isLoadingMore ? <Loading size={16} color="inherit" /> : null
								}>
								{seeMoreText}
							</Button>
						</Box>
					) : null}
				</FormControl>
			) : null}
		</Fragment>
	);
};
